/**
 * Time on page, for the founder's stats page (deploy/cloudflare-worker/worker.js, "TIME ON SITE").
 *
 * Each time this page is hidden (another tab chosen, the window minimised, the page left or
 * closed), the browser tells the edge Worker's `POST /e` how many whole seconds the page was
 * VISIBLE since it last said so:
 *
 *     {"event":"page_time","path":"/convert-to-srt","seconds":42}
 *
 * The Worker adds them to this page's step on the visitor's path for the day (migrations/0007),
 * under the daily number it already gave them. Nothing new is stored in the browser: no cookie, no
 * storage, no id.
 *
 * ONLY VISIBLE TIME. The clock runs while `document.visibilityState` is 'visible' and stops when it
 * is not, so a page left in a background tab counts nothing. One page view counts at most
 * MAX_PAGE_SECONDS (2 hours) in all: a tab left open on a screen overnight is not a night on the
 * site. Under a second is not sent; the part of a second left over waits for the next hide.
 *
 * ONCE PER HIDE. Leaving a page fires `visibilitychange` (hidden) and `pagehide`; whichever comes
 * second finds nothing new to send. A page brought back from the back-forward cache starts its
 * clock again on `pageshow`.
 *
 * WHAT GOES. `location.pathname` and the seconds, nothing else: no query, no hash, no title, no
 * referrer. The rules of lib/usage.ts hold: the `tw-no-stats` flag sends nothing, a browser
 * without `sendBeacon` sends nothing, and nothing here can throw into the page.
 */

import { countingOff, PAGE_TIME_EVENT, USAGE_ENDPOINT } from './usage.ts';

/** The most one page view counts, in seconds: 2 hours. The Worker refuses more. */
export const MAX_PAGE_SECONDS = 7200;
/** A path the Worker takes: a slash, then up to 200 characters, none a space, `?` or `#`. */
export const PAGE_PATH = /^\/[^\s?#]{0,200}$/;

/**
 * The beacon's body, or null when it would be refused: a path outside PAGE_PATH, or under a
 * whole second. Seconds are whole, rounded down, and never over MAX_PAGE_SECONDS.
 */
export function pageTimeBody(path: string, seconds: number): string | null {
  if (typeof path !== 'string' || !PAGE_PATH.test(path)) return null;
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return null;
  const whole = Math.min(Math.floor(seconds), MAX_PAGE_SECONDS);
  if (whole < 1) return null;
  return JSON.stringify({ event: PAGE_TIME_EVENT, path, seconds: whole });
}

/** Send one body, unless this browser asked not to be counted. Never throws. */
function beacon(body: string): void {
  try {
    if (countingOff()) return;
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
    navigator.sendBeacon(USAGE_ENDPOINT, body);
  } catch {
    /* counting never surfaces */
  }
}

/** What the clock listens to and reads; the page's own by default, a stand-in in tests. */
export interface PageTimerHost {
  document: { readonly visibilityState: string; addEventListener: Document['addEventListener'] };
  window: { addEventListener: Window['addEventListener'] };
  /** Milliseconds from a clock that never goes back. */
  now: () => number;
  path: string;
  send: (body: string) => void;
}

/** The page's clock, for a caller that wants to send what it has now. */
export interface PageTimer {
  /** Stop the clock and send the visible time not yet sent, if it is a second or more. */
  flush(): void;
}

function pageHost(): PageTimerHost | null {
  if (typeof document === 'undefined' || typeof window === 'undefined') return null;
  if (typeof location === 'undefined' || typeof performance === 'undefined') return null;
  return {
    document,
    window,
    now: () => performance.now(),
    path: location.pathname,
    send: beacon,
  };
}

/**
 * Start this page's clock. Returns null, and does nothing, where there is no page to time or
 * anything goes wrong.
 */
export function startPageTimer(host?: PageTimerHost | null): PageTimer | null {
  try {
    const timed = host === undefined ? pageHost() : host;
    if (timed === null) return null;
    const { document: doc, window: win, now, path, send } = timed;
    let visibleSince: number | null = doc.visibilityState === 'visible' ? now() : null;
    /** Visible milliseconds not sent yet. */
    let unsent = 0;
    /** Whole seconds sent for this page view, toward MAX_PAGE_SECONDS. */
    let sent = 0;

    const start = (): void => {
      if (visibleSince === null) visibleSince = now();
    };
    const stop = (): void => {
      if (visibleSince === null) return;
      unsent += Math.max(0, now() - visibleSince);
      visibleSince = null;
    };
    const flush = (): void => {
      try {
        stop();
        const seconds = Math.min(Math.floor(unsent / 1000), MAX_PAGE_SECONDS - sent);
        const body = pageTimeBody(path, seconds);
        if (body === null) return;
        unsent -= seconds * 1000;
        sent += seconds;
        send(body);
      } catch {
        /* counting never surfaces */
      }
    };

    doc.addEventListener('visibilitychange', () => {
      if (doc.visibilityState === 'visible') start();
      else flush();
    });
    win.addEventListener('pagehide', flush);
    win.addEventListener('pageshow', () => {
      if (doc.visibilityState === 'visible') start();
    });
    return { flush };
  } catch {
    return null;
  }
}
